import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  cancelCompanyDeletion,
  deleteCompanyLogo,
  fetchCompanyLogoUrl,
  fetchCurrentCompany,
  scheduleCompanyDeletion,
  updateCompany,
  uploadCompanyLogo,
} from '../../api/company';
import { fetchCompanyPlanStatus, updateCompanyPlan } from '../../api/companyPlan';
import { useCompanyStore } from '../../store/companyStore';
import { Company, CompanyPlanStatus } from '../../types/company';
import { companyRoleLabel } from '../../types/companyRole';
import './CompanySettingsPanel.css';
import { reportError } from '../../utils/notify';

function errMessage(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback
  );
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  return value.slice(0, 10);
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 16).replace('T', ' ');
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const DELETION_CONFIRM_TEXT = '영구 삭제';

function CompanySettingsPanel() {
  const setCompany = useCompanyStore((state) => state.setCompany);
  const setPlan = useCompanyStore((state) => state.setPlan);
  const role = useCompanyStore((state) => state.role);
  const isOwner = role === 'OWNER';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const logoUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [companyId, setCompanyId] = useState<number | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [businessNumber, setBusinessNumber] = useState('');
  const [representativeName, setRepresentativeName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [faxNumber, setFaxNumber] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [hasLogo, setHasLogo] = useState(false);
  const [logoName, setLogoName] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [myRole, setMyRole] = useState(role ?? '');
  const [planStatus, setPlanStatus] = useState<CompanyPlanStatus | null>(null);
  const [deletionPending, setDeletionPending] = useState(false);
  const [deletionScheduledAt, setDeletionScheduledAt] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletionConfirm, setDeletionConfirm] = useState('');
  const [deletionBusy, setDeletionBusy] = useState(false);

  const revokeLogoUrl = useCallback(() => {
    if (logoUrlRef.current) {
      window.URL.revokeObjectURL(logoUrlRef.current);
      logoUrlRef.current = null;
    }
    setLogoUrl(null);
  }, []);

  const applyCompany = useCallback(
    (company: Company) => {
      setCompanyId(company.companyId);
      setCompanyName(company.companyName ?? '');
      setBusinessNumber(company.businessNumber ?? '');
      setRepresentativeName(company.representativeName ?? '');
      setPhoneNumber(company.phoneNumber ?? '');
      setFaxNumber(company.faxNumber ?? '');
      setEmail(company.email ?? '');
      setWebsite(company.website ?? '');
      setAddress(company.address ?? '');
      setDescription(company.description ?? '');
      setHasLogo(Boolean(company.hasLogo));
      setLogoName(company.logoName ?? null);
      setCreatedAt(company.createdAt ?? null);
      setMemberCount(company.memberCount ?? null);
      setMyRole(company.role ?? '');
      setDeletionPending(Boolean(company.deletionPending));
      setDeletionScheduledAt(company.deletionScheduledAt ?? null);
      setCompany({
        companyId: company.companyId,
        companyName: company.companyName,
        role: company.role,
        plan: company.plan ?? undefined,
      });
    },
    [setCompany]
  );

  const loadLogoPreview = useCallback(
    async (withLogo: boolean) => {
      revokeLogoUrl();
      if (!withLogo) return;
      try {
        const url = await fetchCompanyLogoUrl();
        logoUrlRef.current = url;
        setLogoUrl(url);
      } catch {
        setLogoUrl(null);
      }
    },
    [revokeLogoUrl]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [company, plan] = await Promise.all([
        fetchCurrentCompany(),
        fetchCompanyPlanStatus().catch(() => null),
      ]);
      applyCompany(company);
      if (plan) {
        setPlanStatus(plan);
        setPlan(plan.plan);
      }
      await loadLogoPreview(Boolean(company.hasLogo));
    } catch (err) {
      reportError(setError, errMessage(err, '회사 정보를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [applyCompany, loadLogoPreview, setPlan]);

  useEffect(() => {
    void load();
    return () => revokeLogoUrl();
  }, [load, revokeLogoUrl]);

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!isOwner) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const updated = await updateCompany({
        companyName: companyName.trim(),
        businessNumber: businessNumber.trim(),
        phoneNumber: phoneNumber.trim(),
        email: email.trim(),
        address: address.trim() || undefined,
        representativeName: representativeName.trim() || undefined,
        faxNumber: faxNumber.trim() || undefined,
        website: website.trim() || undefined,
        description: description.trim() || undefined,
      });
      applyCompany(updated);
      setSuccess('회사 정보를 저장했습니다.');
    } catch (err) {
      reportError(setError, errMessage(err, '회사 정보 저장에 실패했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (file: File | null) => {
    if (!file || !isOwner) return;
    try {
      setUploading(true);
      setError('');
      setSuccess('');
      const updated = await uploadCompanyLogo(file);
      applyCompany(updated);
      await loadLogoPreview(Boolean(updated.hasLogo));
      setSuccess('로고를 등록했습니다. 검사성적서 PDF 상단에 반영됩니다.');
    } catch (err) {
      reportError(setError, errMessage(err, '로고 업로드에 실패했습니다.'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    if (!isOwner || !hasLogo) return;
    try {
      setUploading(true);
      setError('');
      setSuccess('');
      await deleteCompanyLogo();
      setHasLogo(false);
      setLogoName(null);
      revokeLogoUrl();
      setSuccess('로고를 삭제했습니다.');
    } catch (err) {
      reportError(setError, errMessage(err, '로고 삭제에 실패했습니다.'));
    } finally {
      setUploading(false);
    }
  };

  const handlePlanChange = async (next: 'STANDARD' | 'PRO') => {
    if (!isOwner || planSaving) return;
    if (planStatus?.plan === next) return;
    try {
      setPlanSaving(true);
      setError('');
      setSuccess('');
      const updated = await updateCompanyPlan(next);
      setPlanStatus(updated);
      setPlan(updated.plan);
      setSuccess(`요금제를 ${updated.planLabel}(으)로 변경했습니다. (데모)`);
    } catch (err) {
      reportError(setError, errMessage(err, '요금제 변경에 실패했습니다.'));
    } finally {
      setPlanSaving(false);
    }
  };

  const openDeleteModal = () => {
    setDeletionConfirm('');
    setDeleteModalOpen(true);
    setError('');
    setSuccess('');
  };

  const handleScheduleDeletion = async () => {
    if (!isOwner || deletionBusy) return;
    if (deletionConfirm.trim() !== DELETION_CONFIRM_TEXT) {
      setError(`확인을 위해 '${DELETION_CONFIRM_TEXT}'를 정확히 입력해 주세요.`);
      return;
    }
    try {
      setDeletionBusy(true);
      setError('');
      setSuccess('');
      const updated = await scheduleCompanyDeletion(DELETION_CONFIRM_TEXT);
      applyCompany(updated);
      setDeleteModalOpen(false);
      setDeletionConfirm('');
      setSuccess(
        `회사 삭제가 예약되었습니다. ${formatDateTime(updated.deletionScheduledAt)}까지 취소할 수 있습니다.`
      );
    } catch (err) {
      reportError(setError, errMessage(err, '회사 삭제 예약에 실패했습니다.'));
    } finally {
      setDeletionBusy(false);
    }
  };

  const handleCancelDeletion = async () => {
    if (!isOwner || deletionBusy) return;
    try {
      setDeletionBusy(true);
      setError('');
      setSuccess('');
      const updated = await cancelCompanyDeletion();
      applyCompany(updated);
      setSuccess('회사 삭제 예약을 취소했습니다. 다시 정상적으로 사용할 수 있습니다.');
    } catch (err) {
      reportError(setError, errMessage(err, '삭제 예약 취소에 실패했습니다.'));
    } finally {
      setDeletionBusy(false);
    }
  };

  return (
    <main className="company-settings">
      {loading ? (
        <p className="company-settings__hint">불러오는 중...</p>
      ) : (
        <form className="company-settings__layout" onSubmit={handleSave}>
          {deletionPending ? (
            <div className="company-settings__danger-banner company-settings__danger-banner--top">
              <div>
                <strong>이 회사는 삭제 예정입니다</strong>
                <p>
                  {formatDateTime(deletionScheduledAt)}까지 오너가 취소할 수 있습니다. 유예 중
                  멤버는 접근할 수 없습니다.
                </p>
              </div>
              {isOwner ? (
                <button
                  type="button"
                  className="company-settings__btn company-settings__btn--primary"
                  disabled={deletionBusy}
                  onClick={() => void handleCancelDeletion()}
                >
                  {deletionBusy ? '처리 중...' : '삭제 취소'}
                </button>
              ) : null}
            </div>
          ) : null}
          <section className="company-settings__hero">
            <div className="company-settings__hero-brand">
              <div className="company-settings__hero-logo">
                {logoUrl ? <img src={logoUrl} alt="회사 로고" /> : <span>LOGO</span>}
              </div>
              <div className="company-settings__hero-text">
                <div className="company-settings__hero-top">
                  <h2 className="company-settings__title">{companyName || '회사 관리'}</h2>
                  <span className="company-settings__role">{companyRoleLabel(myRole)}</span>
                </div>
                <p className="company-settings__desc">
                  회사 프로필 · 로고 · 연락처를 관리합니다. 로고와 회사명은 인수·중간검사 PDF 헤더에
                  표시됩니다.
                </p>
              </div>
            </div>
            <div className="company-settings__metrics">
              <div className="company-settings__metric">
                <span>구성원</span>
                <strong>{memberCount != null ? `${memberCount}명` : '-'}</strong>
              </div>
              <div className="company-settings__metric">
                <span>개설일</span>
                <strong>{formatDate(createdAt)}</strong>
              </div>
              <div className="company-settings__metric">
                <span>회사 ID</span>
                <strong>{companyId ?? '-'}</strong>
              </div>
              <div className="company-settings__metric">
                <span>요금제</span>
                <strong>{planStatus?.planLabel || '-'}</strong>
              </div>
              <div className="company-settings__metric">
                <span>로고</span>
                <strong className={hasLogo ? 'is-on' : ''}>{hasLogo ? '등록됨' : '미등록'}</strong>
              </div>
            </div>
          </section>

          <section className="company-settings__panel">
            <header className="company-settings__panel-head">
              <div>
                <h3>요금제</h3>
                <p>
                  스탠다드: PDF 하루 10건 · 게스트 발급 불가. 프로: PDF 무제한 · 게스트 발급 · 감사
                  로그(오너).
                  {planStatus?.forceAllPro
                    ? ' 현재 런칭 프로모션으로 전 회사가 프로입니다.'
                    : ''}
                </p>
              </div>
            </header>
            <div className="company-settings__grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="company-settings__field">
                <span>현재</span>
                <p style={{ margin: '6px 0 0', fontSize: 14 }}>
                  <strong>{planStatus?.planLabel || '-'}</strong>
                  {planStatus && !planStatus.guestIssuanceEnabled ? (
                    <span style={{ marginLeft: 8, color: '#64748b' }}>
                      · 오늘 PDF {planStatus.pdfUsedToday}/{planStatus.pdfDailyLimit}건
                      {planStatus.pdfRemainingToday != null
                        ? ` (남은 ${planStatus.pdfRemainingToday}건)`
                        : ''}
                    </span>
                  ) : planStatus?.guestIssuanceEnabled ? (
                    <span style={{ marginLeft: 8, color: '#64748b' }}>
                      · PDF 무제한
                      {planStatus.forceAllPro ? ' · 프로모션' : ''}
                    </span>
                  ) : null}
                </p>
              </div>
              {isOwner && planStatus?.planChangeAllowed ? (
                <div className="company-settings__btn-row">
                  <button
                    type="button"
                    className="company-settings__btn company-settings__btn--ghost"
                    disabled={planSaving || planStatus?.plan === 'STANDARD'}
                    onClick={() => void handlePlanChange('STANDARD')}
                  >
                    스탠다드
                  </button>
                  <button
                    type="button"
                    className="company-settings__btn company-settings__btn--primary"
                    disabled={planSaving || planStatus?.plan === 'PRO'}
                    onClick={() => void handlePlanChange('PRO')}
                  >
                    {planSaving ? '변경 중...' : '프로로 변경 (데모)'}
                  </button>
                </div>
              ) : isOwner && planStatus?.forceAllPro ? (
                <p className="company-settings__note">
                  런칭 기간에는 요금제 변경이 없습니다. 결제 오픈 후 스탠다드 기본 · 프로 업그레이드
                  로 전환됩니다.
                </p>
              ) : (
                <p className="company-settings__note">요금제 변경은 오너만 가능합니다.</p>
              )}
            </div>
          </section>

          <section className="company-settings__panel">
            <header className="company-settings__panel-head">
              <div>
                <h3>브랜드 / 로고</h3>
                <p>성적서·문서에 쓰일 회사 로고입니다. jpg, png, webp, gif · 최대 5MB</p>
              </div>
            </header>
            <div className="company-settings__logo-block">
              <div className="company-settings__logo-preview">
                {logoUrl ? <img src={logoUrl} alt="회사 로고 미리보기" /> : <span>로고 미리보기</span>}
              </div>
              <div className="company-settings__logo-actions">
                <p className="company-settings__logo-meta">
                  {hasLogo ? logoName || '등록된 로고' : '아직 등록된 로고가 없습니다.'}
                </p>
                {isOwner ? (
                  <div className="company-settings__btn-row">
                    <button
                      type="button"
                      className="company-settings__btn company-settings__btn--primary"
                      disabled={uploading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? '처리 중...' : hasLogo ? '로고 변경' : '로고 등록'}
                    </button>
                    {hasLogo ? (
                      <button
                        type="button"
                        className="company-settings__btn company-settings__btn--ghost"
                        disabled={uploading}
                        onClick={() => void handleDeleteLogo()}
                      >
                        삭제
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <p className="company-settings__note">로고 변경은 오너만 가능합니다.</p>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  hidden
                  onChange={(e) => void handleLogoChange(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>
          </section>

          <section className="company-settings__panel">
            <header className="company-settings__panel-head">
              <div>
                <h3>기본 정보</h3>
                <p>사업자 정보와 대표자 정보입니다.</p>
              </div>
            </header>
            <div className="company-settings__grid">
              <label className="company-settings__field">
                <span>회사명 *</span>
                <input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  disabled={!isOwner}
                  required
                  maxLength={255}
                />
              </label>
              <label className="company-settings__field">
                <span>사업자등록번호 *</span>
                <input
                  value={businessNumber}
                  onChange={(e) => setBusinessNumber(e.target.value)}
                  disabled={!isOwner}
                  required
                  maxLength={20}
                  placeholder="000-00-00000"
                />
              </label>
              <label className="company-settings__field">
                <span>대표자명</span>
                <input
                  value={representativeName}
                  onChange={(e) => setRepresentativeName(e.target.value)}
                  disabled={!isOwner}
                  maxLength={100}
                  placeholder="대표이사 성명"
                />
              </label>
              <label className="company-settings__field company-settings__field--full">
                <span>주소</span>
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  disabled={!isOwner}
                  maxLength={255}
                  placeholder="사업장 주소"
                />
              </label>
            </div>
          </section>

          <section className="company-settings__panel">
            <header className="company-settings__panel-head">
              <div>
                <h3>연락처</h3>
                <p>대외 연락과 문서 표기에 사용합니다.</p>
              </div>
            </header>
            <div className="company-settings__grid">
              <label className="company-settings__field">
                <span>대표 전화 *</span>
                <input
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  disabled={!isOwner}
                  required
                  maxLength={20}
                />
              </label>
              <label className="company-settings__field">
                <span>팩스</span>
                <input
                  value={faxNumber}
                  onChange={(e) => setFaxNumber(e.target.value)}
                  disabled={!isOwner}
                  maxLength={30}
                />
              </label>
              <label className="company-settings__field">
                <span>이메일 *</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!isOwner}
                  required
                  maxLength={255}
                />
              </label>
              <label className="company-settings__field">
                <span>웹사이트</span>
                <input
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  disabled={!isOwner}
                  maxLength={255}
                  placeholder="https://"
                />
              </label>
            </div>
          </section>

          <section className="company-settings__panel">
            <header className="company-settings__panel-head">
              <div>
                <h3>회사 소개</h3>
                <p>선택 입력입니다. 최대 1000자까지 저장할 수 있습니다.</p>
              </div>
            </header>
            <label className="company-settings__field">
              <span>소개 / 메모</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!isOwner}
                maxLength={1000}
                rows={5}
                placeholder="회사 소개, 사업 분야, 내부 메모 등을 적어 두세요."
              />
              <em className="company-settings__counter">{description.length} / 1000</em>
            </label>
          </section>

          {(error || success) && (
            <div className="company-settings__alerts">
              {error ? <p className="company-settings__error">{error}</p> : null}
              {success ? <p className="company-settings__success">{success}</p> : null}
            </div>
          )}

          {isOwner ? (
            <div className="company-settings__footer">
              <p className="company-settings__note">* 표시는 필수 항목입니다.</p>
              <button
                type="submit"
                className="company-settings__btn company-settings__btn--primary company-settings__btn--lg"
                disabled={saving || deletionPending}
              >
                {saving ? '저장 중...' : '변경사항 저장'}
              </button>
            </div>
          ) : (
            <p className="company-settings__hint">회사 정보 수정은 오너만 가능합니다.</p>
          )}

          {isOwner ? (
            <section className="company-settings__danger">
              <header className="company-settings__panel-head">
                <div>
                  <h3>위험 구역</h3>
                  <p>
                    회사와 관련 데이터는 삭제 예약 후 7일이 지나면 영구 삭제됩니다. 그 전에는
                    오너만 취소할 수 있으며, 유예 중 멤버는 접근할 수 없습니다.
                  </p>
                </div>
              </header>
              {deletionPending ? (
                <div className="company-settings__danger-banner">
                  <div>
                    <strong>삭제 예정</strong>
                    <p>
                      {formatDateTime(deletionScheduledAt)}에 영구 삭제됩니다. 그 전에 취소하면
                      회사를 계속 사용할 수 있습니다.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="company-settings__btn company-settings__btn--primary"
                    disabled={deletionBusy}
                    onClick={() => void handleCancelDeletion()}
                  >
                    {deletionBusy ? '처리 중...' : '삭제 취소'}
                  </button>
                </div>
              ) : (
                <div className="company-settings__danger-row">
                  <p className="company-settings__danger-copy">
                    삭제 예약 즉시 멤버 접근이 차단되고, 7일 후 복구할 수 없습니다.
                  </p>
                  <button
                    type="button"
                    className="company-settings__btn company-settings__btn--danger"
                    onClick={openDeleteModal}
                  >
                    회사 삭제
                  </button>
                </div>
              )}
            </section>
          ) : null}
        </form>
      )}

      {deleteModalOpen ? (
        <div
          className="company-settings__modal-backdrop"
          role="presentation"
          onClick={() => {
            if (!deletionBusy) setDeleteModalOpen(false);
          }}
        >
          <div
            className="company-settings__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="company-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="company-delete-title">회사 영구 삭제</h3>
            <p>
              <strong>{companyName || '이 회사'}</strong>를 삭제 예약합니다. 7일 유예 후 영구
              삭제되며, 유예 중에는 멤버 접근이 제한됩니다.
            </p>
            <label className="company-settings__field">
              <span>
                확인을 위해 <em>{DELETION_CONFIRM_TEXT}</em>를 입력하세요
              </span>
              <input
                value={deletionConfirm}
                onChange={(e) => setDeletionConfirm(e.target.value)}
                placeholder={DELETION_CONFIRM_TEXT}
                autoFocus
                disabled={deletionBusy}
              />
            </label>
            <div className="company-settings__modal-actions">
              <button
                type="button"
                className="company-settings__btn company-settings__btn--ghost"
                disabled={deletionBusy}
                onClick={() => setDeleteModalOpen(false)}
              >
                닫기
              </button>
              <button
                type="button"
                className="company-settings__btn company-settings__btn--danger"
                disabled={deletionBusy || deletionConfirm.trim() !== DELETION_CONFIRM_TEXT}
                onClick={() => void handleScheduleDeletion()}
              >
                {deletionBusy ? '예약 중...' : '삭제 예약'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}

export default CompanySettingsPanel;
